import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import {
  PedidoConsultaDTO,
  ItemDePedidoConsultaDTO,
  VarianteDePrecoDeItemPedidoDTO,
  ModificadorDePrecoDeItemPedidoDTO,
} from '../../../../models/pedidos/pedido.model';
import { EstadoDePedido, ESTADO_PEDIDO_CONFIG } from '../../../../enums/EstadoDePedido.enum';
import { ACAO_ENTREGAR, calcularEstadosDisponiveis } from '../pedido-estados-dialog/pedido-estados-dialog.component';
import { itemFoiCancelado, itemFoiFaturado, pedidoEstaEncerrado } from '../../utils/pedido-item-status.util';
import { ehNoEstabelecimento, resolverTipoEntrega, TIPO_ENTREGA_CONFIG } from '../../../../enums/TipoDeEntrega.enum';
import { PermissaoService } from '../../../../services/permissao/permissao.service';

/** Estados em que o pedido ainda esta em andamento e o timer deve ser exibido */
const ESTADOS_ATIVOS: EstadoDePedido[] = [
  EstadoDePedido.ABERTO,
  EstadoDePedido.EM_PRODUCAO,
  EstadoDePedido.A_CAMINHO,
  EstadoDePedido.AGUARDANDO_RETIRADA,
];

@Component({
  selector: 'app-pedido-card',
  templateUrl: './pedido-card.component.html',
  styleUrls: ['./pedido-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PedidoCardComponent implements OnInit, OnDestroy {

  /** Texto formatado do timer ("X min" ou "Xh Ymin") */
  tempoDecorrido = '';

  /** Indica se o tempo ultrapassou 30 minutos */
  timerCritico = false;

  /** Referencia ao setInterval para limpeza */
  private timerRef: ReturnType<typeof setInterval> | null = null;

  @Input() pedido!: PedidoConsultaDTO;
  @Input() texts: any = {};

  // Configuracao de moeda da empresa (propagada pelo componente pai).
  // Defaults Real (BRL) protegem contra render antes da chegada da sessao.
  @Input() moedaPadrao: 'BRL' | 'PYG' | 'USD' | 'ARS' = 'BRL';
  @Input() localeMoeda = 'pt-BR';
  @Input() digitsMoeda = '1.2-2';

  @Output() abrirDetalhe = new EventEmitter<PedidoConsultaDTO>();
  @Output() abrirWhatsApp = new EventEmitter<PedidoConsultaDTO>();
  @Output() compartilhar = new EventEmitter<PedidoConsultaDTO>();
  @Output() avancarEstado = new EventEmitter<{ pedido: PedidoConsultaDTO; acao: string }>();
  @Output() faturar = new EventEmitter<PedidoConsultaDTO>();
  @Output() finalizar = new EventEmitter<PedidoConsultaDTO>();
  /** Entrega direta a partir do card; o pai confirma antes de executar. */
  @Output() entregar = new EventEmitter<PedidoConsultaDTO>();
  /** Atalho: abre o seletor de estados direto da listagem. */
  @Output() abrirEstados = new EventEmitter<PedidoConsultaDTO>();

  constructor(
    private cdr: ChangeDetectorRef,
    private permissaoService: PermissaoService,
  ) {}

  /**
   * Operar o pedido (avancar estado, faturar, finalizar, entregar, cancelar) exige
   * GESTAO_DE_PEDIDOS. Ver o pedido exige apenas VISUALIZAR_PEDIDOS, checada na
   * entrada da rota — por isso o card e montado por quem so consulta e sao as ACOES
   * que somem, nao a tela. Mesma divisao do mobile (pedido_card.dart).
   */
  get podeOperarPedido(): boolean {
    return this.permissaoService.temPermissao('gestaoDePedidos');
  }

  // =========================================================================
  // Lifecycle
  // =========================================================================

  ngOnInit(): void {
    if (this.isEstadoAtivo) {
      this.atualizarTimer();
      this.timerRef = setInterval(() => this.atualizarTimer(), 60000);
    }
  }

  ngOnDestroy(): void {
    if (this.timerRef) {
      clearInterval(this.timerRef);
      this.timerRef = null;
    }
  }

  // =========================================================================
  // Timer
  // =========================================================================

  /** Verifica se o estado do pedido e ativo (timer visivel) */
  get isEstadoAtivo(): boolean {
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);
    return ESTADOS_ATIVOS.includes(estado);
  }

  /** Recalcula tempo decorrido e atualiza propriedades */
  atualizarTimer(): void {
    const info = this.calcularTempoDecorrido(this.pedido?.emissao);
    this.tempoDecorrido = info.texto;
    this.timerCritico = info.critico;
    this.cdr.markForCheck();
  }

  /** Calcula diferenca entre agora e a emissao do pedido */
  calcularTempoDecorrido(emissao?: string): { texto: string; critico: boolean } {
    if (!emissao) return { texto: '', critico: false };

    const agora = Date.now();
    const inicio = new Date(emissao).getTime();
    const diffMs = agora - inicio;
    const diffMin = Math.floor(diffMs / 60000);

    // Protecao contra valores negativos (emissao no futuro)
    if (diffMin < 0) return { texto: '0 min', critico: false };

    const critico = diffMin >= 30;

    if (diffMin < 60) {
      return { texto: `${diffMin} min`, critico };
    }

    const horas = Math.floor(diffMin / 60);
    const minutos = diffMin % 60;
    return { texto: `${horas}h ${minutos}min`, critico };
  }

  /**
   * Resolve o estado do pedido para string.
   * O backend serializa enums como objeto { enum: "ABERTO", nome: "Aberto", ... }
   */
  /** Pedido sem linha ativa (FINALIZADO/CANCELADO): as linhas liquidadas nao esmaecem. */
  pedidoEncerrado(): boolean {
    return pedidoEstaEncerrado(this.resolveEstado(this.pedido?.estadoDePedido));
  }

  private resolveEstado(estado: any): EstadoDePedido {
    if (typeof estado === 'string') return estado as EstadoDePedido;
    if (estado?.enum) return estado.enum as EstadoDePedido;
    if (estado?.name) return estado.name as EstadoDePedido;
    return EstadoDePedido.ABERTO;
  }

  getCorEstado(estado: any): string {
    return ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)]?.cor || '#999';
  }

  getBackgroundEstado(estado: any): string {
    const config = ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)];
    return config?.gradiente || config?.cor || '#999';
  }

  getIconeEstado(estado: any): string {
    return ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)]?.icone || 'pi pi-circle';
  }

  getEstadoLabel(estado: any): string {
    const resolved = this.resolveEstado(estado);
    const label = this.texts?.estados?.[resolved];
    if (label && typeof label === 'string') return label;
    // Fallback: usar o campo 'nome' do objeto backend
    if (typeof estado === 'object' && estado?.nome) return estado.nome;
    return resolved;
  }

  formatarDataHora(dataStr?: string): string {
    if (!dataStr) return '';
    try {
      const data = new Date(dataStr);
      const dataFormatada = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const horaFormatada = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      return `${dataFormatada} ${horaFormatada}`;
    } catch {
      return '';
    }
  }

  onCardClick(): void {
    this.abrirDetalhe.emit(this.pedido);
  }

  onWhatsAppClick(event: Event): void {
    event.stopPropagation();
    this.abrirWhatsApp.emit(this.pedido);
  }

  onCompartilharClick(event: Event): void {
    event.stopPropagation();
    this.compartilhar.emit(this.pedido);
  }

  // =========================================================================
  // Avanco rapido de estado
  // =========================================================================

  /**
   * Resolve o campo tipoEntrega que pode vir como string ou objeto LocalizableEnum.
   * Padrao identico ao usado em PedidoDetalheDialogComponent.estadosDisponiveis().
   */
  private resolverTipoEntrega(): string {
    const tipo = this.pedido?.tipoEntrega;
    if (!tipo) return '';
    if (typeof tipo === 'string') return tipo;
    if (tipo?.enum) return tipo.enum;
    if (tipo?.name) return tipo.name;
    return '';
  }

  // =========================================================================
  // Badge de tipo de entrega
  // =========================================================================

  /** Retorna true se o pedido tem um tipo de entrega conhecido. */
  temTipoEntrega(): boolean {
    return resolverTipoEntrega(this.pedido?.tipoEntrega) !== null;
  }

  /** Retorna true quando o pedido e do tipo DELIVERY */
  ehDelivery(): boolean {
    return this.resolverTipoEntrega() === 'DELIVERY';
  }

  /** Label traduzida do tipo de entrega (Retirada / Delivery) */
  getTipoEntregaLabel(): string {
    const tipo = this.resolverTipoEntrega();
    const fromTexts = this.texts?.tipo_entrega?.[tipo];
    if (fromTexts) return fromTexts;
    // Fallback: usar campo 'nome' do objeto LocalizableEnum
    const tipoObj: any = this.pedido?.tipoEntrega;
    if (tipoObj && typeof tipoObj === 'object' && tipoObj.nome) return tipoObj.nome;
    return tipo;
  }

  /** Icone do tipo de entrega para exibir no badge */
  getTipoEntregaIcone(): string {
    const tipo = resolverTipoEntrega(this.pedido?.tipoEntrega);
    return tipo ? TIPO_ENTREGA_CONFIG[tipo].icone : 'pi pi-tag';
  }

  /** Classe CSS do badge conforme o tipo (define cor) */
  getTipoEntregaClasse(): string {
    const tipo = resolverTipoEntrega(this.pedido?.tipoEntrega);
    return tipo ? TIPO_ENTREGA_CONFIG[tipo].classe : '';
  }

  // =========================================================================
  // Origem do pedido (Mini Shop / Cardapio Digital)
  // =========================================================================

  /** Retorna 'MINI_SHOP' | null inferido da observacao (ambas as origens sao Mini Shop) */
  getOrigemPedido(): 'MINI_SHOP' | null {
    const obs = this.pedido?.observacao?.trim();
    if (!obs) return null;
    const prefixos = [
      'Pedido via Mini Shop',
      'Pedido via Cardapio Digital',
      'Pedido via Cardápio Digital',
    ];
    return prefixos.some(p => obs.startsWith(p)) ? 'MINI_SHOP' : null;
  }

  /** Label traduzido da origem, via texts.origem.* do pai */
  getOrigemLabel(): string {
    const origem = this.getOrigemPedido();
    if (!origem) return '';
    return this.texts?.origem?.[origem] || 'Mini Shop';
  }

  /** Icone da origem */
  getOrigemIcone(): string {
    return this.getOrigemPedido() ? 'pi pi-shop' : '';
  }

  /**
   * Observacao "limpa" para exibir no card.
   *
   * Pedidos vindos do Cardapio Digital / Mini Shop tem a observacao montada
   * pelo backend como "Pedido via Mini Shop | DELIVERY | Pagamento: X | comentario".
   * Quando o badge de tipo de entrega ja esta exibido, as duas primeiras
   * partes ("Pedido via ..." + tipo) sao redundantes — removemos para deixar
   * apenas Pagamento / Mesa / comentario do cliente. Se nao sobrar nada,
   * retorna string vazia (o *ngIf no template esconde a linha).
   */
  getObservacaoFormatada(): string {
    const obs = this.pedido?.observacao?.trim();
    if (!obs) return '';

    const prefixos = [
      'Pedido via Mini Shop',
      'Pedido via Cardapio Digital',
      'Pedido via Cardápio Digital',
    ];
    const temPrefixo = prefixos.some(p => obs.startsWith(p));
    if (!temPrefixo) return obs;

    // Remove primeiras 2 partes (prefixo + tipo de pedido)
    const partes = obs.split(' | ').slice(2);
    return partes.join(' | ').trim();
  }

  /** Retorna true se o estado atual permite avanco rapido (ABERTO ou EM_PRODUCAO) */
  temBotaoAvanco(): boolean {
    if (!this.podeOperarPedido) return false;
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);
    return estado === EstadoDePedido.ABERTO || estado === EstadoDePedido.EM_PRODUCAO;
  }

  /** Retorna true se o estado atual permite cancelamento */
  temBotaoCancelar(): boolean {
    if (!this.podeOperarPedido) return false;
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);
    return (
      estado === EstadoDePedido.ABERTO ||
      estado === EstadoDePedido.EM_PRODUCAO ||
      estado === EstadoDePedido.A_CAMINHO ||
      estado === EstadoDePedido.AGUARDANDO_RETIRADA
    );
  }

  /** Endpoint do service correspondente a cada estado de destino. */
  private static readonly ACAO_POR_DESTINO: Record<string, string> = {
    [EstadoDePedido.EM_PRODUCAO]: 'enviarParaProducao',
    [EstadoDePedido.A_CAMINHO]: 'enviarACaminho',
    [EstadoDePedido.AGUARDANDO_RETIRADA]: 'enviarAguardandoRetirada',
  };

  /**
   * Estado para onde o botao de avanco leva. Icone, cor e acao derivam daqui,
   * entao os tres nunca saem de sincronia.
   */
  private getEstadoAvanco(): EstadoDePedido | null {
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);

    if (estado === EstadoDePedido.ABERTO) {
      return EstadoDePedido.EM_PRODUCAO;
    }

    if (estado === EstadoDePedido.EM_PRODUCAO) {
      // Quem consome ou retira no estabelecimento nunca vai "a caminho" — so o delivery sai.
      return ehNoEstabelecimento(this.pedido?.tipoEntrega)
        ? EstadoDePedido.AGUARDANDO_RETIRADA
        : EstadoDePedido.A_CAMINHO; // DELIVERY ou fallback
    }

    return null;
  }

  /** Retorna a acao correspondente ao proximo estado */
  getAcaoAvanco(): string {
    const destino = this.getEstadoAvanco();
    return destino ? PedidoCardComponent.ACAO_POR_DESTINO[destino] || '' : '';
  }

  /** Icone do estado de destino, para o botao anunciar aonde leva. */
  getIconeAvanco(): string {
    const destino = this.getEstadoAvanco();
    return destino ? ESTADO_PEDIDO_CONFIG[destino].icone : '';
  }

  /**
   * Cor do estado de destino. Botao de TRANSICAO e a excecao a cor semantica de
   * acao: sem isso o mesmo glifo sairia com cores diferentes no card e no
   * filtro. Faturar e compartilhar, que nao sao transicao, seguem a cor da acao.
   */
  getCorAvanco(): string {
    const destino = this.getEstadoAvanco();
    return destino ? ESTADO_PEDIDO_CONFIG[destino].cor : '';
  }

  /** Retorna o label curto para o botao de avanco */
  getLabelAvanco(): string {
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);

    if (estado === EstadoDePedido.ABERTO) {
      return this.texts?.botoes?.enviar_producao || 'Produção';
    }

    if (estado === EstadoDePedido.EM_PRODUCAO) {
      if (ehNoEstabelecimento(this.pedido?.tipoEntrega)) {
        return this.texts?.botoes?.aguardando_retirada || 'Retirada';
      }
      return this.texts?.botoes?.enviar_a_caminho || 'A Caminho';
    }

    return '';
  }

  /** Retorna o tooltip traduzido para o botao de avanco */
  getTooltipAvanco(): string {
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);

    if (estado === EstadoDePedido.ABERTO) {
      return this.texts?.tooltips?.avancar_producao || '';
    }

    if (estado === EstadoDePedido.EM_PRODUCAO) {
      if (ehNoEstabelecimento(this.pedido?.tipoEntrega)) {
        return this.texts?.tooltips?.avancar_aguardando_retirada || '';
      }
      return this.texts?.tooltips?.avancar_a_caminho || '';
    }

    return '';
  }

  /** Handler do clique no botao de avanco. Usa stopPropagation para nao abrir o dialog */
  onAvancarEstadoClick(event: Event): void {
    event.stopPropagation();
    const acao = this.getAcaoAvanco();
    if (acao) {
      this.avancarEstado.emit({ pedido: this.pedido, acao });
    }
  }

  /** Handler do clique no botao de cancelar. Usa stopPropagation para nao abrir o dialog */
  onCancelarClick(event: Event): void {
    event.stopPropagation();
    this.avancarEstado.emit({ pedido: this.pedido, acao: 'cancelar' });
  }

  // =========================================================================
  // Faturamento rapido
  // =========================================================================

  /**
   * Retorna true se o pedido pode ser faturado: qualquer estado em andamento, com saldo > 0.
   *
   * ABERTO entra na lista porque faturar um pedido que a cozinha ainda nao comecou e permitido — o
   * backend nunca olhou o estado, so o saldo (`PedidoService.validarSaldoParaFaturamento`).
   */
  temBotaoFaturar(): boolean {
    if (!this.podeOperarPedido) return false;
    // Delivery pago online e ja faturado: oferecer "faturar" aqui so levaria o operador a um
    // erro do backend. A comanda de balcao, que fatura em partes, nao entra nesta regra.
    if (this.pedido?.vendaFechadaPorPagamentoOnline) return false;
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);
    const estadoPermitido = estado === EstadoDePedido.ABERTO
        || estado === EstadoDePedido.EM_PRODUCAO
        || estado === EstadoDePedido.A_CAMINHO
        || estado === EstadoDePedido.AGUARDANDO_RETIRADA
        || estado === EstadoDePedido.ENTREGUE;
    if (!estadoPermitido) return false;
    return this.pedido?.itens?.some(i => (i.saldo || 0) > 0) ?? false;
  }

  /** Handler do clique no botao de faturar */
  onFaturarClick(event: Event): void {
    event.stopPropagation();
    this.faturar.emit(this.pedido);
  }

  // =========================================================================
  // Finalizar rapido (ENTREGUE)
  // =========================================================================

  /**
   * Retorna true se o pedido pode ser finalizado: sem saldo pendente e num estado
   * que permita a acao.
   *
   * Alem de ENTREGUE, aceita EM_PRODUCAO quando NAO e delivery. Motivo: um pedido
   * de balcao totalmente faturado ficava sem saida nenhuma — nao da para faturar
   * (saldo zero), nao da para finalizar (nao e ENTREGUE) e o cancelamento e
   * recusado por ja ter nota. ENTREGUE so e alcancavel a partir de A_CAMINHO /
   * AGUARDANDO_RETIRADA, um caminho desenhado para delivery.
   *
   * O dominio ja aceitava essa transicao: `Pedido.finalizar()` exige apenas saldo
   * zero e que o pedido nao esteja CANCELADO/FINALIZADO — era a tela que estava
   * mais restritiva que a API.
   *
   * AGUARDANDO_RETIRADA ficou de fora de proposito: ja tem saida por "Entregar e
   * Finalizar", e inclui-lo faria o pedido pular o estado ENTREGUE sem necessidade.
   */
  temBotaoFinalizar(): boolean {
    if (!this.podeOperarPedido) return false;
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);
    const estadoPermite =
      estado === EstadoDePedido.ENTREGUE ||
      (estado === EstadoDePedido.EM_PRODUCAO && !this.ehDelivery());
    if (!estadoPermite) return false;
    return this.pedido?.itens?.every(i => (i.saldo ?? 0) === 0) ?? false;
  }

  /** Handler do clique no botao de finalizar */
  onFinalizarClick(event: Event): void {
    event.stopPropagation();
    this.finalizar.emit(this.pedido);
  }

  // =========================================================================
  // Entrega rapida
  // =========================================================================

  /**
   * Entregar so existe a partir de A_CAMINHO / AGUARDANDO_RETIRADA — mesma
   * condicao que o seletor de estados usa para oferecer a opcao
   * (pedido-estados-dialog.component.ts:89).
   * Nao olha saldo de proposito: o seletor tambem nao olha, e mudar isso
   * alteraria comportamento fora do escopo aprovado.
   */
  temBotaoEntregar(): boolean {
    if (!this.podeOperarPedido) return false;
    const estado = this.resolveEstado(this.pedido?.estadoDePedido);
    return estado === EstadoDePedido.A_CAMINHO
        || estado === EstadoDePedido.AGUARDANDO_RETIRADA;
  }

  /** Handler do clique no botao de entregar. stopPropagation evita abrir o detalhe. */
  onEntregarClick(event: Event): void {
    event.stopPropagation();
    this.entregar.emit(this.pedido);
  }

  /**
   * So aparece se sobrar transicao que os botoes do card nao executem direto.
   * Em ABERTO, avancar e cancelar ja cobrem as duas opcoes; em A_CAMINHO e
   * AGUARDANDO_RETIRADA, cancelar e entregar cobrem as duas opcoes comuns e o
   * seletor so permanece quando "Entregar e Finalizar" esta disponivel
   * (pedido sem saldo), que e a unica acao exclusiva do menu.
   */
  temAtalhoEstados(): boolean {
    if (!this.podeOperarPedido) return false;
    const opcoes = calcularEstadosDisponiveis(this.pedido, this.texts);
    if (!opcoes.length) return false;

    const cobertasPeloCard = new Set<string>();
    if (this.temBotaoAvanco()) cobertasPeloCard.add(this.getAcaoAvanco());
    if (this.temBotaoCancelar()) cobertasPeloCard.add('cancelar');
    if (this.temBotaoEntregar()) cobertasPeloCard.add(ACAO_ENTREGAR);

    return opcoes.some(opcao => !cobertasPeloCard.has(opcao.acao));
  }

  /** Handler do clique no atalho de estados */
  onAbrirEstadosClick(event: Event): void {
    event.stopPropagation();
    this.abrirEstados.emit(this.pedido);
  }

  // ---------------------------------------------------------------------------
  // Itens do pedido (variantes / modificadores / observação) — mesma exibição
  // do diálogo de detalhe, resumida no card.
  // ---------------------------------------------------------------------------

  /** Itens do pedido (vazio quando a listagem não trouxe itens). */
  get itensDoPedido(): ItemDePedidoConsultaDTO[] {
    return this.pedido?.itens ?? [];
  }

  /**
   * Situacao de faturamento para a tag do card:
   *   'total'   → tudo que restava foi faturado
   *   'parcial' → parte faturada, ainda ha saldo em aberto
   *   null      → nada faturado (sem tag)
   *
   * Le `quantidadeFaturada`, NAO `saldo`: saldo tambem zera por cancelamento,
   * entao `saldo === 0` marcaria um pedido cancelado como faturado.
   */
  get statusFaturamento(): 'total' | 'parcial' | null {
    const itens = this.itensDoPedido;
    if (!itens.length) return null;

    const faturado = itens.reduce((soma, i) => soma + (i.quantidadeFaturada || 0), 0);
    if (faturado <= 0) return null;

    const aindaTemSaldo = itens.some(i => (i.saldo || 0) > 0);
    return aindaTemSaldo ? 'parcial' : 'total';
  }

  /** Nome de exibição do item (catálogo ou cardápio digital). */
  nomeDoItem(item: ItemDePedidoConsultaDTO): string {
    return item?.item?.nome || item?.produtoCardapioNome || '-';
  }

  // ---------------------------------------------------------------------------
  // Por que a linha do item esta apagada — mesma leitura do dialogo de detalhe.
  // A regra mora em pedido-item-status.util para as duas telas nao divergirem.
  // ---------------------------------------------------------------------------

  /** Linha esgotada cuja quantidade TODA foi cancelada. */
  linhaCancelada(item: ItemDePedidoConsultaDTO): boolean {
    return itemFoiCancelado(item);
  }

  /** Linha esgotada com algum faturamento (mista conta como faturada). */
  linhaFaturada(item: ItemDePedidoConsultaDTO): boolean {
    return itemFoiFaturado(item);
  }

  formatarVariantes(variantes?: VarianteDePrecoDeItemPedidoDTO[] | null, quantidadeDoItem?: number): string {
    if (!variantes || variantes.length === 0) return '';
    return variantes
      .map(v => {
        const nome = v.descricao || v.codigoDaVariante || '-';
        const porUnidade = this.quantidadePorUnidade(v.quantidade, quantidadeDoItem);
        return porUnidade > 1 ? `${nome} (x${porUnidade})` : nome;
      })
      .join(', ');
  }

  formatarModificadores(modificadores?: ModificadorDePrecoDeItemPedidoDTO[] | null, quantidadeDoItem?: number): string {
    if (!modificadores || modificadores.length === 0) return '';
    return modificadores
      .map(m => {
        const nome = m.descricao || m.codigoDoModificador || '-';
        const porUnidade = this.quantidadePorUnidade(m.quantidade, quantidadeDoItem);
        return porUnidade > 1 ? `${nome} (x${porUnidade})` : nome;
      })
      .join(', ');
  }

  /**
   * A quantidade da variante/modificador guardada no pedido e o TOTAL da linha (por unidade x
   * quantidade do item). O "(xN)" mostra so o que vai em CADA unidade — a quantidade do item ja
   * aparece na linha. Sem `quantidadeDoItem` (item ainda nao confirmado), ja e por unidade.
   */
  private quantidadePorUnidade(quantidade?: number, quantidadeDoItem?: number): number {
    if (!quantidade) return 0;
    return quantidadeDoItem ? Math.round(quantidade / quantidadeDoItem) : quantidade;
  }
}
